import type {
  ColumnFragment,
  ControlCardInstanceFullFragment,
  ValueFragment,
  ValueInstanceFragment
} from "app/gql/graphqlSchema";
import {IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon, IonInput} from "@ionic/react";
import {assertNotNil, isEqual, isNil, isNotBlank} from "app/utils/stdlib";
import {useSaveControlCardValue} from "app/employee/controlCard/hook/useSaveControlCardValue";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useTranslation} from "react-i18next";
import {SavingProgress} from "app/employee/controlCard/input/SavingProgress";
import {transformToApplicationError} from "app/gql/handleGraphqlErrors";
import {setApplicationError} from "app/utils/parseErrorResult";
import {useCallback, useEffect, useMemo, useRef} from "react";
import {DisplayRootError} from "app/employee/controlCard/input/DisplayRootError";
import {refMapping} from "app/employee/controlCard/refUtils";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {IdSingleComboBox} from "app/employee/controlCard/IdSingleComboBox";
import type {MaybeNil} from "app/utils/types";
import {useForm, useWatch} from "react-hook-form";
import {z} from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {EMPTY_VALUE} from "app/employee/controlCard/types";
import {IonCardFooter} from "app/employee/controlCard/ControlCardUI";
import {checkmarkCircleOutline, chevronForward, closeOutline} from "ionicons/icons";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardEmployeeRefColumn"};
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

const formSchema = z.object({
  value: z.string().nullable()
});

export type FormData = z.infer<typeof formSchema>;

type RefOption = ControlCardInstanceFullFragment["columnRefOptions"][number]["options"][number];

const keyExtractor = (option: RefOption) => option.ref.id;
const textExtractor = (option: RefOption) => option.employee.name;
const secondaryTextExtractor = (option: RefOption) => option.employee.position?.name ?? null;
const searchTextExtractor = (option: RefOption) =>
  [option.employee.name, option.employee.position?.name].filter(isNotBlank).join(" ");
const isOptionEqualToSelectedId = (option: RefOption, id: string) => option.ref.id === id;

export const InputEmployeeRef = ({column, valueInstance, disabled}: Props) => {
  const {t} = useTranslation();
  const {instance, isComplete, canEdit} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);
  const comboBoxRef = useRef<HTMLIonModalElement | null>(null);

  const {saveValue, saving} = useSaveControlCardValue(valueInstance);

  let value: string | null = null;
  const persistedValue = valueInstance.value;
  if (persistedValue.__typename === "ControlCardRefValue") {
    value = persistedValue.id;
  }

  const {
    control,
    formState: {isDirty, isSubmitting, disabled: formDisabled, errors},
    setValue,
    handleSubmit,
    reset,
    setError
  } = useForm<FormData>({
    values: {value},
    disabled: isComplete || !canEdit || !!disabled,
    resolver: zodResolver(formSchema)
  });

  const options = useMemo<RefOption[]>(() => {
    const colRefOpt = instance.columnRefOptions.find((c) => isEqual(c.column.key, column.key));
    assertNotNil(colRefOpt);
    const {filter} = refMapping[colRefOpt.__typename];
    return colRefOpt.options.filter((o) => filter(o) || o.ref.id === value);
  }, [column.key, instance.columnRefOptions, value]);

  const currentValue = useWatch({control, name: "value"});

  const selectedName = useMemo(
    () => options.find((o) => o.ref.id === currentValue)?.employee.name ?? null,
    [options, currentValue]
  );

  const handleSave = useMemo(
    () =>
      handleSubmit(async (data) => {
        try {
          let saveData: ValueFragment;
          if (isNil(data.value)) {
            saveData = EMPTY_VALUE;
          } else {
            saveData = {__typename: "ControlCardRefValue", id: data.value};
          }
          await saveValue(saveData);
        } catch (e) {
          const appError = transformToApplicationError(e);
          setApplicationError(appError, setError);
        }
      }),
    [handleSubmit, saveValue, setError]
  );

  const handleClear = () => reset();

  useEffect(() => {
    if (isComplete) {
      reset();
    }
  }, [isComplete, reset]);

  const handleSelect = useCallback(
    async (option: MaybeNil<RefOption>) => {
      setValue("value", option?.ref.id ?? null, {shouldDirty: true, shouldValidate: true});
      await Promise.allSettled([handleSave(), comboBoxRef.current?.dismiss()]);
    },
    [handleSave, setValue]
  );

  return (
    <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
      <IonCardHeader>
        <IonCardTitle>{column.title}</IonCardTitle>
        {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
      </IonCardHeader>
      <IonCardContent>
        <DisplayRootError errors={errors} />
        <IonInput
          disabled={formDisabled}
          fill="outline"
          label={column.title}
          labelPlacement="stacked"
          onClick={!formDisabled ? () => comboBoxRef.current?.present() : undefined}
          placeholder={t("g.select-employee")}
          readonly
          value={selectedName ?? ""}
        >
          <IonIcon icon={chevronForward} slot="end" />
        </IonInput>
        <DisplayValueInstanceError column={column} disabled={formDisabled} valueInstance={valueInstance} />
      </IonCardContent>
      {isDirty && !isSubmitting && (
        <IonCardFooter>
          <IonButton color="primary" fill="outline" onClick={handleClear} size="small">
            <IonIcon icon={closeOutline} slot="start" />
            {t("g.cancel")}
          </IonButton>
          <IonButton color="primary" disabled={formDisabled} onClick={handleSave} size="small">
            <IonIcon icon={checkmarkCircleOutline} slot="start" />
            {t("g.save")}
          </IonButton>
        </IonCardFooter>
      )}
      <SavingProgress isSubmitting={saving} />
      <FstIonModal ref={comboBoxRef}>
        <IdSingleComboBox
          isEqual={isOptionEqualToSelectedId}
          keyExtractor={keyExtractor}
          onSelect={handleSelect}
          options={options}
          searchPlaceholder={t("g.search-employee-name-or-position")}
          searchTextExtractor={searchTextExtractor}
          secondaryTextExtractor={secondaryTextExtractor}
          textExtractor={textExtractor}
          title={column.title}
          value={currentValue}
        />
      </FstIonModal>
    </InputIonCard>
  );
};
